import {
  Control,
  UseFormGetValues,
  UseFormReset,
  UseFormSetValue,
  UseFormTrigger,
} from "react-hook-form";
import { FormControl, Tooltip } from "@mui/material";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import React, { useEffect, useState } from "react";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import { debounce, isNil } from "lodash";

import { AddressFormInput } from "../../../../../components/formComponents/AddressFormInput";
import { characters } from "../../../../../constants/Patterns";
import { FormInputDropdown } from "../../../../../components/formComponents/FormInputDropdown";
import { FormInputText } from "../../../../../components/formComponents/FormInputText";
import { GENDER_DROPDOWN_OPTIONS } from "../../../../../constants/Constants";
import fontWeight from "../../../../../styles/mui/fontWeight";
import {
  AddressDetailsContainerBody,
  AddressHeader,
  horizontalDivider,
  IoIosPinIcon,
  ParentGridBody,
  PatientDetailsContainerBody,
  PatientErrorTooltip,
  PatientInputHolder,
  SummaryContainerHead,
  SummaryHeadPatientName,
  SummaryHeadTexts,
  SummaryItemContainer,
} from "../styles/styles";
import { colors } from "../../../../../styles/colors";
import {
  PatientDetailsInfo,
  SavePatientInformationActionDispatchTypes,
} from "../../../../../constants/Patient";
import { rules } from "../../../../../utils/validation/Validation";
import DatePicker from "../../../../../components/formComponents/DatePicker";
import Permission from "../../../../auth/Permission";
import { PatientRecordsAccess } from "../../../../../constants/Permission";
import { EligibilityData } from "../../../../../models/EligibilityData";
import { StoreFilterData } from "../../../../../models/StoreFilterData";
import { SavePatientInformation } from "../../../../../models/Patient";
import { LanguageMasterResponse } from "../../../../../models/Api/Master";
import { getList, scrollTop } from "../../../../../utils";
import { SavePatientInformationResponse } from "../../../../../models/Api/Patient";
import { NotesTooltipStyle } from "../../AuthorizationDetails/styles/NotesTable";

export interface AddressInfo {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  values: SavePatientInformation;
  trigger: UseFormTrigger<any>;
  isManual: boolean;
  eligibilityRecord: EligibilityData;
  storeFilter: StoreFilterData;
  reset: UseFormReset<any>;
  getValues: UseFormGetValues<any>;
  getLanguageMasterResp: () => void;
  LanguageMasterData: LanguageMasterResponse;
  patientInfoData: SavePatientInformationResponse;
  resetStates: (actionType: string[]) => void;
}

type AllProps = AddressInfo;

const PatientDetailsForm: React.FC<AllProps> = ({
  control,
  setValue,
  values,
  trigger,
  isManual,
  storeFilter,
  reset,
  getValues,
  patientInfoData,
  resetStates,
  getLanguageMasterResp,
  LanguageMasterData,
}: AllProps) => {
  const { loading: LoadingLanguage, response: LanguageResponse } =
    LanguageMasterData;

  const [infoErrorMsg, setInfoErrorMsg] = useState<string>("");
  const { error: patientInformationError } = patientInfoData;

  useEffect(() => {
    const debounceEffect = debounce(() => {
      if (
        !isNil(patientInformationError) &&
        Object.prototype.hasOwnProperty.call(
          patientInformationError,
          "duplicateMBISubscriberId"
        )
      ) {
        const mbiErrorMsg = patientInformationError.duplicateMBISubscriberId;
        scrollTop();
        setInfoErrorMsg(mbiErrorMsg);
      }
    }, 500);
    debounceEffect();
  }, [patientInformationError]);

  useEffect(() => {
    if (!LoadingLanguage) {
      getLanguageMasterResp();
    }
    return () => {
      resetStates([
        SavePatientInformationActionDispatchTypes.SAVE_PATIENT_INFORMATION_RESET,
      ]);
    };
  }, []);

  const maxDate = dayjs();
  useEffect(() => {
    if (storeFilter) {
      reset({
        ...getValues(),
        createPatient: {
          lastName: storeFilter?.lastName || "",
          firstName: storeFilter?.firstName || "",
          dateOfBirth: storeFilter?.dateOfBirth || "",
        },
      });
    }
  }, [storeFilter]);

  return (
    <>
      <Grid size={12} sx={SummaryItemContainer}>
        <Grid sx={ParentGridBody}>
          <Grid container sx={SummaryContainerHead}>
            <Grid size={12}>
              <Grid container sx={SummaryHeadTexts}>
                <Grid size={12}>
                  <Box sx={SummaryHeadPatientName}>
                    <Typography
                      variant="h6"
                      color={colors.fonts[20]}
                      fontWeight={fontWeight.Weight[6]}
                      align="left"
                    >
                      Patient Details
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="body2"
                      color={colors.fonts[5]}
                      fontWeight={fontWeight.Weight[3]}
                      align="left"
                    >
                      {PatientDetailsInfo}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={5} pt={"1.2rem"}>
                  <Tooltip
                    title={
                      <Typography
                        variant="subtitle1"
                        fontWeight={fontWeight.Weight[4]}
                        sx={{ textAlign: "right" }}
                      >
                        <Box
                          dangerouslySetInnerHTML={{
                            __html: infoErrorMsg,
                          }}
                        />
                      </Typography>
                    }
                    arrow
                    slotProps={{
                      popper: {
                        sx: NotesTooltipStyle,
                      },
                    }}
                  >
                    <Typography
                      color={colors.red[100]}
                      sx={PatientErrorTooltip}
                    >
                      {infoErrorMsg}
                    </Typography>
                  </Tooltip>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Permission
            controlId={`|${PatientRecordsAccess.PATIENT_DETAILS_FORM_READ}|${PatientRecordsAccess.PATIENT_DETAILS_FORM_UPDATE}`}
            passThrough
          >
            {(readOnly: boolean) => (
              <>
                <Grid container sx={PatientDetailsContainerBody}>
                  <Grid size={2.3}>
                    <Grid container sx={PatientInputHolder("15rem")}>
                      <FormInputText
                        name="createPatient.lastName"
                        control={control}
                        label="Last Name:"
                        helper={rules.patientLastNameRequired}
                        pattern={characters}
                        textLength={64}
                        readOnly={readOnly}
                        shouldTrimOnBlur={true}
                      />
                    </Grid>
                  </Grid>
                  <Grid size={2.3}>
                    <Grid container sx={PatientInputHolder("15rem")}>
                      <FormInputText
                        name="createPatient.middleName"
                        control={control}
                        label="Middle Name:"
                        helper={rules.middleName}
                        textLength={64}
                        readOnly={readOnly}
                        shouldTrimOnBlur={true}
                      />
                    </Grid>
                  </Grid>
                  <Grid size={2.3}>
                    <Grid container sx={PatientInputHolder("15rem")}>
                      <FormInputText
                        name="createPatient.firstName"
                        fieldrequired="required"
                        control={control}
                        label="First Name:"
                        helper={rules.patientFirstNameRequired}
                        textLength={64}
                        readOnly={readOnly}
                        shouldTrimOnBlur={true}
                      />
                    </Grid>
                  </Grid>
                  <Grid size={1.5}>
                    <Grid container sx={PatientInputHolder("8rem")}>
                      <FormInputDropdown
                        name="createPatient.gender"
                        fieldrequired="required"
                        control={control}
                        label="Gender:"
                        helper={rules.gender}
                        list={GENDER_DROPDOWN_OPTIONS}
                        readOnly={readOnly}
                      />
                    </Grid>
                  </Grid>
                  <Grid size={1.6}>
                    <Grid container sx={PatientInputHolder("10rem")}>
                      <FormControl variant="standard">
                        <DatePicker
                          name="createPatient.dateOfBirth"
                          control={control}
                          label={"Date Of Birth:"}
                          helper={rules.dateOfBirth}
                          readOnly={readOnly}
                          maximumdate={maxDate}
                        />
                      </FormControl>
                    </Grid>
                  </Grid>
                  <Grid size={1}>
                    <Grid container sx={PatientInputHolder("8rem")}>
                      <FormInputDropdown
                        name="createPatient.languagePreferenceValue"
                        control={control}
                        label="Language:"
                        list={getList(LanguageResponse, {
                          label: "languageName",
                          value: "languageName",
                        })}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid container>
                  <Grid size={12}>
                    <Divider sx={horizontalDivider} />
                  </Grid>
                </Grid>
                <Grid container sx={AddressDetailsContainerBody}>
                  <Grid size={12}>
                    <Grid container pl={"1rem"}>
                      <Grid size={0.2} pt={"0.5rem"}>
                        <IoIosPinIcon />
                      </Grid>
                      <Grid size={2.2}>
                        <Box sx={AddressHeader}>
                          <Typography
                            variant="h5"
                            fontWeight={fontWeight.Weight[5]}
                            color={colors.fonts[4]}
                            align="left"
                          >
                            ADDRESS DETAILS:
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid size={12}>
                    <AddressFormInput
                      control={control}
                      setValue={setValue}
                      values={values}
                      trigger={trigger}
                      isRequired={isManual}
                    />
                  </Grid>
                </Grid>
              </>
            )}
          </Permission>
        </Grid>
      </Grid>
    </>
  );
};
export default PatientDetailsForm;
